import { DirectiveOptions, VNode } from 'vue';
import { DirectiveBinding } from 'vue/types/options';
import Logger from '@owc-utils/common/Logger';
import { TEST_ID_ATTR_NAME, ENVIRONMENT_WHITELIST, MESSAGES } from './TestId.config';

/**
 * Extracts Vue components name from virtual node tag name because
 * the components name is not accessible by default.
 * @param context Vue component
 * @returns component's name as string
 */
const getVueComponentName = (context: Vue): string => {
  if (!context) return '';

  const tagname = context.$vnode.tag;
  return tagname ? tagname.replace(/vue-component-([\d]+-)?/, '') : '';
};

/**
 * To ensure uniqueness we need to check if a test-id already exists.
 * It could be the case in a list where all the list items has the same test-id
 *
 * @param testId test-id which need to be checked for existence
 * @returns true if the given test-id exists already otherwise false
 */
const isTestIdAlreadyPresent = (testId: string): boolean => {
  const existingTestIdEl = document.querySelector(`[${TEST_ID_ATTR_NAME}="${testId}"]`);
  return Boolean(existingTestIdEl);
};

/**
 * The test-id should only be visible in `development` and `test` environments.
 * There is no need to render them in `production`.
 *
 * @returns true for every environment except `production`
 */
const isAcceptedEnvironment = (): boolean => {
  const { NODE_ENV } = process.env;

  return NODE_ENV ? ENVIRONMENT_WHITELIST.includes(NODE_ENV.toLowerCase()) : false;
};

/**
 * To ensure consistency all whitespaces will be replaced by `-`
 * and lower case is enforced
 * @param value value passed to v-test-id directive
 * @return escaped value as string
 */
const escapeValue = (value: string): string => {
  return value.toLowerCase().replace(/\s+/g, '-');
};

/**
 * A test-id directive is requested by the QA-Team to make element selection
 * easier.
 * To use this directive you can import it as named js-import. and used it like:
 *
 * ```
 * import { TestId } from '@owc-utils/TestIdDirective';
 *
 * export default ComponentInitializeDispatcher.extend({
 *  name: "owc-component",
 *  directives: {
 *   TestId
 *  }
 * });
 * ```
 *
 * Now you can use `v-test-id` in you template but only in this component.
 *
 * ```
 * <template>
 *  <div v-test-id="'my-fancy-id'" >...</div>
 * </template>
 * ```
 *
 * As default input a string is expected. If this is not the case it will be converted into a string.
 * For all non-prod environments v-test-id will become data-test-id attribute and
 * the value of it will be composed out of the component's name and v-test-id value.
 *
 * ```
 * <div data-test-id="owc-component-my-fancy-id">...</div>
 * ```
 *
 * To ensure uniqueness of test-ids all test-ids are scoped to the component but
 * there is also a check for duplication in place. When a duplicate is detected
 * you will be warned in the browsers console.
 *
 * If you want to use duplicated test-ids for some reason you can pass `.ignore` notifier
 * to v-test-id to suppress the warning
 *
 * ```
 * <template>
 *  <div v-test-id.ignore="'my-fancy-id'" >...</div>
 *  <div v-test-id.ignore="'my-fancy-id'" >...</div>
 * </template>
 * ```
 */
const testIdDirective: DirectiveOptions = {
  inserted(el: HTMLElement, binding: DirectiveBinding, vnode: VNode) {
    if (!isAcceptedEnvironment()) {
      return;
    }

    const { modifiers } = binding;
    const vueComponentName = getVueComponentName(vnode.context as Vue);
    const value = escapeValue(binding.value.toString());
    const testId = `${vueComponentName}-${value}`;

    if (isTestIdAlreadyPresent(testId) && !(modifiers && modifiers.ignore)) {
      Logger.warn(
        MESSAGES.testIdExistsAlready
          .replace(/\${value}/g, value)
          .replace(/\${vueComponentName}/, vueComponentName),
      );
    }

    el.setAttribute(TEST_ID_ATTR_NAME, testId);
  },
};

export default testIdDirective;
